/**
 * Reading a claim out of a token the browser is holding.
 *
 * 🔴 UNVERIFIED, ALWAYS, AND THAT IS NOT A SHORTCUT — IT IS THE ONLY THING THIS
 * SIDE OF THE WIRE CAN DO. The signing secret is server-side, which is the whole
 * design (`tokens.service.ts`: *"a JWT is signed, not encrypted"*). So nothing
 * read here may ever gate a request, decide an authorisation, or stand in for an
 * answer from the API. It exists so a screen can pick a **sentence** or a
 * **menu** — `routes.ts`'s standing distinction, in as many words: *"this is
 * navigation, not authorisation… the backend has been the control since §8.1 and
 * remains it."*
 *
 * ⚠️ ONE IMPLEMENTATION, BECAUSE THERE WERE ABOUT TO BE TWO. `parentSession.ts`
 * already decoded the payload segment to read `exp`; `staffSession.ts` needs the
 * same three lines to read `role`. `audit-query.ts` records the rule the backend
 * reached the same way — *"one implementation of the formula-injection rule
 * rather than two that agreed"* — and two base64url decoders is exactly the
 * shape that ends with one of them untested.
 */

/**
 * The payload segment, parsed. `null` for anything unparseable — a token shape
 * we do not recognise is *"we do not know"*, never a value we invented.
 */
export function decodeJwtClaims(token: string): Record<string, unknown> | null {
  /*
   * 🔴 A NON-STRING IS `null`, NOT A `TypeError`, AND THAT DISTINCTION COST AN
   * AFTERNOON.
   *
   * `POST /auth/login` no longer always returns a token pair: an account with a
   * second factor answers `{ mfaRequired, mfaToken }` and one that must enrol
   * answers `{ mfaEnrolmentRequired, enrolmentToken }`. Passing either of those
   * through `setStaffSession` reaches this function with `undefined`, and
   * `undefined.split` threw — a TypeError carries no `status`, so every screen's
   * error mapping fell through to its last branch and told the operator
   * **"Couldn't reach Kalqy. Check the connection and try again."** about a
   * request that had succeeded with a 200.
   *
   * The callers discriminate the union properly now. This is the layer that
   * makes getting it wrong fail CLOSED — "not a token we can read" — rather than
   * as a network fault, which is the same rule the `catch` below already states.
   */
  if (typeof token !== 'string') return null

  const segments = token.split('.')
  if (segments.length !== 3) return null

  try {
    const claims: unknown = JSON.parse(base64UrlDecode(segments[1]))
    if (typeof claims !== 'object' || claims === null || Array.isArray(claims)) return null
    return claims as Record<string, unknown>
  } catch {
    /*
     * `atob` throws on a malformed segment and `JSON.parse` throws on malformed
     * JSON. Both are the same answer: this is not a token we can read.
     */
    return null
  }
}

/**
 * `exp`, as a date. Seconds since the epoch per RFC 7519, milliseconds here.
 *
 * ⚠️ A UI HINT AND NOTHING MORE. A caller must not withhold a token because our
 * own clock disagrees with it — a laptop with the wrong date would sign a
 * teacher out of a working session, which is the class of bug the ingest path
 * clamps around with `stampedAt`.
 */
export function jwtExpiry(token: string): Date | null {
  const exp = decodeJwtClaims(token)?.exp
  if (typeof exp !== 'number' || !Number.isFinite(exp)) return null
  return new Date(exp * 1000)
}

/**
 * base64url → a binary string.
 *
 * ⚠️ `atob` RETURNS LATIN-1, so a multi-byte character in a claim would come
 * back mangled. That is acceptable here and stated rather than left to be
 * discovered: `AccessTokenPayload` carries `sub`, `role`, `schoolId` and `imp` —
 * cuids and enum names, all ASCII by construction — and this module is not a
 * general-purpose JWT reader. A claim carrying a person's name would be a claim
 * carrying personal data into a signed-not-encrypted token, which
 * `tokens.service.ts` forbids for a larger reason than encoding.
 */
function base64UrlDecode(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
  return atob(padded)
}
